import { useEffect } from "react"
import styled, { css, keyframes } from "styled-components"
import logo from "../assets/peplogo.png"
import playStoreIcon from "../assets/play-store.png"
import appStoreIcon from "../assets/app-store.png"
import heroScreen from "../assets/store/06-hero.jpg"
import momentsScreen from "../assets/store/01-moments.jpg"
import vibesScreen from "../assets/store/02-vibes.jpg"
import plansScreen from "../assets/store/03-plans.jpg"
import nearbyScreen from "../assets/store/04-nearby.jpg"

export const storeLinks = {
  play: "https://play.google.com/store/apps/details?id=com.destiny.in",
  app: "https://apps.apple.com/in/app/destiny-nearby-network/id6739805983",
}

const variants = {
  moment: {
    badge: "moment",
    accent: "#5b7cff",
    glow: "#2a3cff",
    title: ["someone shared", "a moment."],
    highlight: "a moment.",
    copy: "Open pepchat to see it, add yours, or start a vibe from it.",
    chips: ["share your day", "friends add theirs"],
    image: momentsScreen,
  },
  profile: {
    badge: "profile",
    accent: "#ff5fa2",
    glow: "#be185d",
    title: ["someone wants", "to vibe with you."],
    highlight: "vibe",
    copy: "Open pepchat to see their profile, their moments and what they're up to.",
    chips: ["say hi", "see their vibes"],
    image: heroScreen,
  },
  plan: {
    badge: "plan",
    accent: "#9b6bff",
    glow: "#7c3aed",
    title: ["you're invited", "to a plan."],
    highlight: "a plan.",
    copy: "Open pepchat to see the details, who's going, and tap \"I'm in\".",
    chips: ["i'm in →", "see who's going"],
    image: plansScreen,
  },
  vibe: {
    badge: "vibe",
    accent: "#ff6a3d",
    glow: "#ff7a1a",
    title: ["you've been invited", "to vibe."],
    highlight: "to vibe.",
    copy: "Open pepchat to see what it's about, who's in, and jump in.",
    chips: ["jump in", "see who's in"],
    image: vibesScreen,
  },
  group: {
    badge: "vibe",
    accent: "#ff6a3d",
    glow: "#ff7a1a",
    title: ["the convo's", "already started."],
    highlight: "already started.",
    copy: "Open pepchat to jump in and drop your take.",
    chips: ["drop your take", "no awkward hey"],
    image: vibesScreen,
  },
  space: {
    badge: "space",
    accent: "#34d399",
    glow: "#0f8f5f",
    title: ["something's happening", "around the corner."],
    highlight: "around the corner.",
    copy: "Open pepchat to see what's on and tap in.",
    chips: ["nearby", "tap in"],
    image: nearbyScreen,
  },
}

const getPlatform = () => {
  const agent = navigator.userAgent || navigator.vendor || window.opera
  if (/android/i.test(agent)) return "android"
  if (/iPad|iPhone|iPod/.test(agent)) return "ios"
  return "desktop"
}

export const openStore = () => {
  window.location.href = getPlatform() === "ios" ? storeLinks.app : storeLinks.play
}

function AppRedirectScreen({ type = "moment", deepLink, details }) {
  const variant = variants[type] || variants.moment
  const platform = getPlatform()

  useEffect(() => {
    if (deepLink && platform !== "desktop") window.location.href = deepLink
  }, [deepLink, platform])

  const openApp = () => {
    if (platform === "desktop") openStore()
    else window.location.href = deepLink
  }

  return (
    <Shell $glow={variant.glow}>
      <Header>
        <Brand href="/">
          <img src={logo} alt="" />
          <span>pepchat</span>
        </Brand>
      </Header>

      <Layout>
        <Copy>
          <Badge $color={variant.accent}>{variant.badge}</Badge>
          <Title $accent={variant.accent}>
            {variant.title.map((line) => {
              if (!line.includes(variant.highlight)) return <span key={line}>{line}</span>
              const [before, after] = line.split(variant.highlight)
              return <span key={line}>{before}<em>{variant.highlight}</em>{after}</span>
            })}
          </Title>
          <Text>{variant.copy}</Text>

          {details?.title ? (
            <DetailCard $accent={variant.accent}>
              {details.tag && <DetailTag $accent={variant.accent}>{details.tag}</DetailTag>}
              <h2>{details.title}</h2>
              {details.description && <p>{details.description}</p>}
            </DetailCard>
          ) : (
            <Chips>
              {variant.chips.map((chip, index) => (
                <Chip key={chip} $accent={variant.accent} $index={index}>{chip}</Chip>
              ))}
            </Chips>
          )}

          <OpenButton type="button" onClick={openApp} $accent={variant.accent}>
            {platform === "desktop" ? "get the app" : "open in pepchat"} <b aria-hidden="true">→</b>
          </OpenButton>

          <Fallback>
            {platform === "desktop"
              ? "pepchat lives on your phone. Grab it below."
              : "Didn't open? You might not have pepchat yet."}
          </Fallback>

          <StoreRow>
            <StoreButton type="button" onClick={() => { window.location.href = storeLinks.app }}>
              <img src={appStoreIcon} alt="" />
              <span><small>Download on the</small><strong>App Store</strong></span>
            </StoreButton>
            <StoreButton type="button" onClick={() => { window.location.href = storeLinks.play }}>
              <img src={playStoreIcon} alt="" />
              <span><small>Get it on</small><strong>Google Play</strong></span>
            </StoreButton>
          </StoreRow>
        </Copy>

        <Shot $glow={variant.glow}>
          <img src={variant.image} alt="" />
        </Shot>
      </Layout>

      <Footer>give people a reason to vibe. · <a href="/">pepchat.in</a></Footer>
    </Shell>
  )
}

export default AppRedirectScreen

const nudge = keyframes`
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(4px); }
`

const display = css`
  font-family: "Inter Tight", "Inter", "Manrope", sans-serif;
  font-weight: 800;
  letter-spacing: -0.045em;
  text-transform: lowercase;
`

const Shell = styled.div`
  width: 100%;
  max-width: 100vw;
  align-self: stretch;
  min-height: 100vh;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  color: #f6f6f7;
  font-family: "Manrope", "Segoe UI", sans-serif;
  background:
    radial-gradient(60% 50% at 15% 0%, ${({ $glow }) => `${$glow}55`}, transparent 70%),
    radial-gradient(50% 40% at 100% 100%, ${({ $glow }) => `${$glow}33`}, transparent 70%),
    #0a0a0c;

  *, *::before, *::after { box-sizing: border-box; }
  button { font: inherit; }
  :focus-visible { outline: 2px solid #fff; outline-offset: 4px; }
  @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
`

const Header = styled.header`
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  padding: 20px 0;
`

const Brand = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  text-decoration: none;
  img { width: 36px; height: 36px; border-radius: 10px; }
  span { ${display} font-size: 1.4rem; }
`

const Layout = styled.main`
  flex: 1;
  width: min(1120px, calc(100% - 32px));
  margin: 0 auto;
  padding: 24px 0 48px;
  display: grid;
  grid-template-columns: 1.1fr .9fr;
  align-items: center;
  gap: clamp(32px, 6vw, 80px);
  @media (max-width: 820px) { grid-template-columns: 1fr; padding-top: 12px; }
`

const Copy = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  @media (max-width: 820px) { align-items: center; text-align: center; }
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 18px;
  border: 1px solid ${({ $color }) => `${$color}66`};
  border-radius: 999px;
  background: ${({ $color }) => `${$color}1a`};
  font-size: .78rem;
  font-weight: 800;
  letter-spacing: .22em;
  text-transform: uppercase;
  &::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: ${({ $color }) => $color}; }
`

const Title = styled.h1`
  ${display}
  margin: 22px 0 0;
  font-size: clamp(2.6rem, 8vw, 4.8rem);
  line-height: .95;
  span { display: block; }
  em { font-style: normal; color: ${({ $accent }) => $accent}; }
`

const Text = styled.p`
  margin: 20px 0 0;
  max-width: 440px;
  color: #d4d4d8;
  font-size: 1.1rem;
  line-height: 1.6;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
  @media (max-width: 820px) { justify-content: center; }
`

const Chip = styled.span`
  ${display}
  letter-spacing: -0.02em;
  padding: 9px 16px;
  border-radius: 999px;
  font-size: 1rem;
  background: ${({ $index, $accent }) => ($index === 0 ? $accent : "#fff")};
  color: #0a0a0c;
  transform: rotate(${({ $index }) => ($index === 0 ? -3 : 2)}deg);
`

const DetailCard = styled.div`
  width: min(100%, 440px);
  margin-top: 26px;
  padding: 20px 22px;
  border-radius: 24px;
  border: 1px solid ${({ $accent }) => `${$accent}44`};
  background: linear-gradient(160deg, ${({ $accent }) => `${$accent}22`}, #141417 60%);
  text-align: left;
  h2 { margin: 0; font-size: 1.3rem; font-weight: 800; line-height: 1.3; overflow-wrap: anywhere; }
  p {
    margin: 8px 0 0;
    color: #c4c4cc;
    line-height: 1.55;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`

const DetailTag = styled.span`
  display: inline-block;
  margin-bottom: 10px;
  padding: 5px 12px;
  border-radius: 999px;
  background: ${({ $accent }) => $accent};
  color: #0a0a0c;
  font-size: .8rem;
  font-weight: 800;
`

const OpenButton = styled.button`
  ${display}
  letter-spacing: -0.02em;
  margin-top: 32px;
  width: min(100%, 380px);
  min-height: 60px;
  border: 0;
  border-radius: 999px;
  background: ${({ $accent }) => $accent};
  color: #0a0a0c;
  font-size: 1.3rem;
  cursor: pointer;
  box-shadow: 0 14px 40px ${({ $accent }) => `${$accent}55`};
  transition: transform .2s ease;
  b { display: inline-block; animation: ${nudge} 1.4s ease-in-out infinite; }
  &:hover { transform: translateY(-2px) rotate(-1deg); }
`

const Fallback = styled.p`margin: 18px 0 0; color: #a1a1aa; font-size: .92rem;`

const StoreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
  @media (max-width: 820px) { justify-content: center; }
`

const StoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 8px 16px 8px 12px;
  border: 1px solid #34343a;
  border-radius: 14px;
  background: #141417;
  color: #fff;
  text-align: left;
  cursor: pointer;
  img { width: 24px; height: 24px; object-fit: contain; }
  small, strong { display: block; }
  small { font-size: .66rem; color: #a1a1aa; }
  strong { font-size: .96rem; }
  &:hover { background: #1c1c20; }
`

const Shot = styled.div`
  justify-self: center;
  width: min(100%, 400px);
  border-radius: 32px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.08);
  box-shadow: 0 30px 120px ${({ $glow }) => `${$glow}66`};
  img { display: block; width: 100%; height: auto; }
  @media (max-width: 820px) { width: min(100%, 340px); }
`

const Footer = styled.footer`
  padding: 24px 16px;
  text-align: center;
  color: #71717a;
  font-size: .86rem;
  a { color: #c4c4cc; }
`
